import { PRODUCT_BYLINE, PRODUCT_NAME } from "@sagot/constants";
import { useId } from "react";
import { cn } from "../lib/cn";

const MARK_SIZE_CLASS = {
  24: "size-6",
  26: "size-[26px]",
  28: "size-7",
  30: "size-[30px]",
  34: "size-[34px]",
} as const;

export type LogoMarkSize = keyof typeof MARK_SIZE_CLASS;

export interface LogoMarkProps {
  size?: LogoMarkSize;
  className?: string;
}

/** CRUD.IT glyph inside the badge (branding/logo/sagot-mark.svg, 1000-unit artwork scaled into the 64 grid). */
const GLYPH_TRANSFORM = "translate(48.3 45) scale(0.01111)";
const GLYPH_PATHS = [
  "M813.81,473.04l-33.6-33.6-2.08-2.09-127.69-127.69-114.72,114.72,127.69,127.69-389.34,389.34c-5.1,5.1-9.5,10.89-12.75,16.92-17.84,31.05-13.67,71.38,12.75,97.8,15.99,15.99,36.62,23.87,57.47,23.87s41.48-7.88,57.24-23.87l441.25-441.25c27.58-44.03,22.25-103.36-16.22-141.83Z",
  "M311.64,655.62l-127.69-127.69,389.34-389.34c5.1-5.1,9.5-10.89,12.75-16.92,17.84-31.05,13.67-71.38-12.75-97.8-15.99-15.99-36.62-23.87-57.47-23.87s-41.48,7.88-57.24,23.87L17.32,465.12c-27.58,44.03-22.25,103.36,16.22,141.83l33.6,33.6,2.08,2.09,127.69,127.69,114.72-114.72Z",
] as const;

/** Brand mark (branding/logo/sagot-mark.svg): speech cloud with the CRUD.IT badge, token colours, flat. Decorative. */
export function LogoMark({ size = 28, className }: LogoMarkProps) {
  const maskId = `sg-gap-${useId().replace(/:/g, "")}`;
  return (
    <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false" className={cn(MARK_SIZE_CLASS[size], "shrink-0", className)}>
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="64" height="64">
          <rect width="64" height="64" fill="white" />
          <circle cx="53" cy="51" r="12.5" fill="black" />
        </mask>
      </defs>
      <g className="fill-brand-500" mask={`url(#${maskId})`}>
        <circle cx="34" cy="26" r="13.5" />
        <circle cx="19.5" cy="35" r="11" />
        <circle cx="48" cy="36" r="10" />
        <rect x="8.5" y="34" width="49.5" height="19" rx="9.5" />
        <path d="M15 48 10.5 60.5 28 51Z" />
      </g>
      <circle cx="53" cy="51" r="9.5" className="fill-brand-500" />
      <g transform={GLYPH_TRANSFORM} className="fill-ink-950">
        {GLYPH_PATHS.map((d) => <path key={d} d={d} />)}
      </g>
    </svg>
  );
}

export interface LogoProps extends LogoMarkProps {
  /** `light` = white wordmark for navy chrome. */
  tone?: "default" | "light";
  showByline?: boolean;
}

/** Lockup (branding/logo/sagot-lockup-*.svg): mark + Manrope wordmark + byline. */
export function Logo({ size = 28, tone = "default", showByline = true, className }: LogoProps) {
  const light = tone === "light";
  return (
    <span className={cn("inline-flex items-center gap-2.5", className)}>
      <LogoMark size={size} />
      <span className="leading-none">
        <span className={cn("block font-sans text-[19px] font-extrabold tracking-tight", light ? "text-white" : "text-fg")}>
          {PRODUCT_NAME}
        </span>
        {showByline ? (
          <span
            className={cn(
              "mt-1 block text-[9px] font-bold uppercase tracking-[0.16em]",
              light ? "text-ink-400" : "text-fg-subtle",
            )}
          >
            {PRODUCT_BYLINE}
          </span>
        ) : null}
      </span>
    </span>
  );
}
